import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Solutions.css';

const Solutions = () => {
  const location = useLocation();

  const solutions = [
    {
      id: '01',
      title: 'Technical Support',
      category: 'Tech Support & IT Helpdesk',
      description:
        "We deliver fast, dependable technical support that keeps business momentum intact. Our team of seasoned experts work as an extension of yours—jumping in the moment an issue appears, diagnosing and resolving problems with the kind of responsiveness you’d expect from someone sitting right beside you. The result is smooth-running systems, minimal downtime, and a partner who helps your operation move at the speed modern business demands.",
      features_label: 'What we provide',
      features: [
        'On-demand tech expertise without the overhead of an I.T. team',
        'Fast, accurate fixes that reduce downtime',
        'Clear communication and reliable guidance',
        'Support that scales as the business grows'
      ],
      benefits_title: 'Common issues we resolve',
      benefits_list: [
        'Network connectivity failures',
        'Slow computers and performance bottlenecks',
        'Email issues and syncing problems',
        'Software installation and configuration errors',
        'Hardware failures and device troubleshooting',
        'Printer and peripheral setup problems',
        'Wi-Fi instability and weak signal coverage',
        'User account, password, and access control issues',
        'Cloud service issues and integration problems'
      ]
    },
    {
      id: '02',
      title: 'Automation and integration',
      category: 'Business Continuity',
      description:
        "Automation and integration turn scattered systems into a smooth, time-saving machine. By connecting tools that should have been talking to each other all along and automating the repetitive tasks that quietly drain hours, businesses free up real bandwidth to focus on what actually moves them forward. With the right automations in place and the right systems working in sync, a company can operate faster, handle more, and unlock capabilities that just aren’t possible when everything is done manually.",
      features_label: 'What we can do for your business',
      features: [
        'Connect systems via API for seamless data flow',
        'Automate repetitive tasks to save time',
        'Streamline workflows for greater efficiency',
        'Boost productivity with smarter processes'
      ],
      benefits_title: 'Common systems we connect',
      benefits_list: [
        'CRM to email marketing platforms',
        'E-commerce platforms to inventory management',
        'Automated lead routing from web forms to sales teams',
        'Proactive alerting from internal systems to your chat-based business application',
        'Automated invoicing and payment reminders',
        'Helpdesk ticketing systems integrated with messaging apps',
        'Financial modelling using data from your ERP',
        'Order processing connectivity (EDI)',
        'Automated reporting dashboards pulling from multiple data sources'
      ]
    },
    {
      id: '03',
      title: 'Web design and hosting',
      category: 'Digital Presence',
      description:
        "At Snap Remedy, we build websites that bring your vision to life while fitting seamlessly into your business workflows. We collaborate closely with you to ensure every feature enhances efficiency, engages your audience, and drives real impact. Every design choice is made with your goals in mind, creating a site that works as hard as you do. Oh, and don’t worry—we’ll host, secure and maintain your website too, so it’s all ready to go from day one.",
      features_label: 'Services we host',
      features: [
        'Websites',
        'Domain Registration & Domain Name Service (DNS)',
        'Email',
        'Cloud Applications'
      ],
      benefits_title: 'Technologies we use:',
      benefits_list: [
        'Wordpress',
        'PHP',
        'React.js',
        'Python / Django',
        'Javascript / JQuery',
        'HTML / CSS',
        'MySQL / PostgreSQL',
        'REST / GraphQL',
        'SSL / TLS'
      ]
    },
    {
      id: '04',
      title: 'Backup and Security',
      category: 'Cybersecurity',
      description:
        "We protect your business operations with a comprehensive backup and security solution built for reliability and peace of mind. All critical business data—sensitive or otherwise—is securely backed up and fully encrypted, keeping it safe both in transit and at rest. Our advanced security system continuously monitors your environment, automatically blocking malicious activity and alerting our team to any attempted threats. This proactive approach ensures we can respond quickly, keeping your critical systems secure, your data protected, and your business running smoothly.",
      features_label: 'How we can protect your business',
      features: [
        'Encrypted, reliable backups that safeguard critical data at every stage',
        'Protection against malicious email threats',
        'Active defense against harmful software',
        'Proactive monitoring that detects issues early and prevents major disruptions'
      ],
      benefits_title: 'Common security scenarios that we fix',
      benefits_list: [
        'Data loss due to hardware failure, theft, fire and flood',
        'Data breaches due to improper access controls',
        'Online threats (Malware, Ransomware, Phishing, Virus)',
        'Spam and bot attacks to your website',
        'Lack of communication when a threat is active',
        'File version control issues',
        'Malicious attacks via email',
        'Lack of password management',
        'Insecure networks'
      ]
    }
  ];

  useEffect(() => {
    const solutionId = location.state?.solutionId;
    if (!solutionId) return;

    const el = document.getElementById(`solution-${solutionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [location.state?.solutionId]);

  const processSteps = [
    {
      number: '01',
      title: 'Reliability',
      description:
        "Reliability is at the heart of how we serve our customers. When you ask for help, you get quick responses, clear communication, and follow-through you can count on. We solve problems with care and consistency, making sure your business keeps moving without unnecessary delays or surprises. For us, reliability isn’t a buzzword—it’s the everyday commitment to show up, deliver what we promise, and be the steady partner your team can depend on."
    },
    {
      number: '02',
      title: 'Ingenuity',
      description:
        "Ingenuity is the spark that turns challenges into progress at Snap Remedy. When a problem doesn’t have an obvious solution—and plenty don’t—we think creatively, adapt quickly, and look for the smartest path forward. It’s not about overcomplicating things or reinventing the wheel; it’s about finding practical, effective solutions that make your technology work better and your day run smoother. This mindset keeps us agile and efficient, helping us solve issues in ways that save you time, reduce friction, and drive real improvement in your business."
    },
    {
      number: '03',
      title: 'Ownership',
      description:
        "Ownership is at the foundation of the lasting relationships we build with our clients. We take full responsibility for delivering results, ensuring reliability, and addressing challenges proactively. This commitment goes beyond individual tasks—it’s about being a trusted partner who consistently supports your business goals. By owning our work and our outcomes, we create confidence, foster long-term collaboration, and help your business thrive with a partner you can rely on."
    },
    {
      number: '04',
      title: 'Teamwork',
      description:
        "Teamwork at Snap Remedy means being as embedded within your business as you need us to be. We collaborate closely with your teams, understanding your workflows, priorities, and goals so our support feels seamless and integrated. Whether it’s day-to-day operations or urgent technical emergencies, we’re ready to step in, provide solutions, and keep everything running smoothly. Our approach balances the reliability of a dedicated internal team with the flexibility of expert support when and where you need it most, ensuring your technology always empowers your business."
    }
  ];

  return (
    <div className="solutions">
      <section className="hero">
        <div className="hero-container">
          <motion.div className="solutions-hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              What We Can Do For Your Business
            </div>
            <h1 className="hero-title">
              IT Solutions Designed to <span className="highlight">Keep Your Business Moving</span>
            </h1>
            <p className="hero-description">
              Snap Remedy works like a fast and knowledgeable extension of your team—solving technology challenges so you can stay locked in on what you do best. Whether you need airtight security, smart integrations, custom web design, or a dependable team of experts to keep your systems humming, we deliver solutions built around real business needs.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn-hero-primary">
                Lets get started
                <span className="btn-arrow">→</span>
              </Link>
            </div>
          </motion.div>
          <div>
            <img
              src="hero-snap-remedy-solutions.svg"
              className="hero-img"
              alt="Illustration of people and a big lightbulb"
            />
          </div>
        </div>
      </section>

      <section className="solutions-grid-section section-spacing">
        <div className="container">
          <div className="solutions-grid">
            {solutions.map((solution) => (
              <motion.div
                key={solution.id}
                id={`solution-${solution.id}`}
                className="solution-card"
              >
                <div className="solution-header">
                  <span className="solution-id">{solution.id}</span>
                  <span className="solution-category">{solution.category}</span>
                </div>
                <h2 className="solution-title">{solution.title}</h2>
                <p className="solution-description">{solution.description}</p>

                <div className="solution-features">
                  <h4>{solution.features_label}:</h4>
                  <br />
                  <ul className="features-list">
                    {solution.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="solution-benefit">
                  <h4>{solution.benefits_title}:</h4>
                  <br />
                  <ul className="features-list">
                    {solution.benefits_list.map((benefit, i) => (
                      <li key={i}>{benefit}</li>
                    ))}
                  </ul>
                </div>

                <Link to="/contact" className="solution-cta">
                  Contact Us →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section section-spacing">
        <div className="container">
          <motion.div className="section-header">
            <h2 className="section-title">What drives us</h2>
            <p className="section-description">
              Our values shape every decision we make and every solution we deliver. They keep us grounded, keep us honest, and ensure that we show up for our customers with the same care and consistency every time. By focusing on clarity, reliability, and genuine partnership, we create an experience that feels steady and trustworthy—not transactional. These values aren’t just a mission-statement ornament; they’re the foundation that guides how we solve problems, support businesses, and build long-term relationships.
            </p>
          </motion.div>

          <div className="process-timeline">
            {processSteps.map((step) => (
              <motion.div key={step.number} className="process-step">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="solutions-cta section-spacing">
        <div className="container">
          <motion.div className="cta-box">
            <h2 className="cta-title">Let’s Get Started.</h2>
            <p className="cta-description">Book a consultation today.</p>
            <div className="cta-actions">
              <Link to="/contact#contact-form" className="btn-cta-primary">
                Contact Us Today
              </Link>
              <Link to="/case-studies" className="btn-cta-secondary">
                See How We Help Others
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;